import React, { FC, useCallback } from "react";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { RasterIcon } from "components/Icon/Icon";
import {
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { createFieldBlurHandler } from "utils/formValidation";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import PriceBlock from "../../../../components/PriceBlock/PriceBlock";
import {
  Comment,
  Form,
  Message,
  MessageWrapper,
  Paragraph,
  Title,
} from "../../../../styles/serviceRequestStyles";
import RequestInfo from "../../../Storage/components/RequestInfo/RequestInfo";
import { UserComment } from "../../../Storage/components/RequestInfo/RequestInfo.styles";
import { getRequestInfoForType } from "../../../Storage/components/ServiceRequests/requestHelper";
import {
  GroupItem,
  GroupItemCost,
  GroupItemHeader,
  Price,
  Spinner,
  SubmitButton,
} from "../../../Storage/components/ServiceRequests/ServiceRequest.styles";
import {
  expectedServiceRequestFormik,
  ExpectedServiceRequestProps,
  getActiveExpectedRequest,
  isServiceTypeActive,
  withExpectedConnect,
} from "./expectedRequestHelper";

const ExpectedServiceRequest: FC<ExpectedServiceRequestProps> = React.memo(
  ({
    isLoading,
    serviceRequestsTypes,
    panelData,
    openRightPanel,
    ...otherProps
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();

    const formik = useFormik(
      expectedServiceRequestFormik({
        t,
        panelData,
        serviceRequestsTypes,
        isLoading,
        openRightPanel,
        ...otherProps,
      }),
    );
    const {
      values,
      touched,
      errors,
      isSubmitting,
      handleChange,
      setFieldValue,
      handleSubmit,
    } = formik;
    const handleBlur = createFieldBlurHandler(formik);

    const handleClose = useCallback(
      () => openRightPanel("EXPECTED_ITEM", panelData),
      [panelData, openRightPanel],
    );

    const requestType = panelData.get("requestType");
    const readOnly = panelData.get("readOnly");

    const dynamicRequestType = getRequestInfoForType(
      serviceRequestsTypes,
      requestType,
    );
    const isDynamicComplexRequest = !!dynamicRequestType?.get("complexType");

    const customTitle = dynamicRequestType?.get("title") ?? requestType;

    let showParagraph = !!dynamicRequestType?.get("description");
    let paragraphText = dynamicRequestType?.get("description") ?? "";
    let showComment = dynamicRequestType?.get("hasComment");
    let showPriceSection: boolean;
    let showMessageIconBlock: boolean;
    let requestCost;

    const hasRequest = isServiceTypeActive(
      panelData,
      serviceRequestsTypes,
      requestType,
    );

    let submitTitle = hasRequest
      ? t("common.cancelRequest")
      : t("serviceRequests.submit");

    if (isDynamicComplexRequest) {
      showPriceSection = false;
      showMessageIconBlock = false;
    } else {
      showPriceSection = true;
      requestCost = dynamicRequestType?.get("cost");
      showMessageIconBlock = true;
    }

    const handleSubmitButtonClick = () => {
      handleSubmit();
    };

    const isCollectionComplexType = (req: any) => !!req.get("collection");

    const handleGroupCheckBoxClick = (groupRequest: any) => {
      if (isCollectionComplexType(groupRequest)) {
        let nextValue: string[] = !values.selectedGroupOption
          ? []
          : (values.selectedGroupOption as string[]);
        if (nextValue.includes(groupRequest.get("type"))) {
          nextValue = nextValue.filter((x) => x !== groupRequest.get("type"));
        } else {
          nextValue.push(groupRequest.get("type"));
        }
        setFieldValue("selectedGroupOption", nextValue);
      } else {
        setFieldValue("selectedGroupOption", groupRequest.get("type"));
      }
    };

    const getIsOptionSelected = (req: any) => {
      return isCollectionComplexType(req)
        ? (values.selectedGroupOption as string[])?.includes(req.get("type"))
        : values.selectedGroupOption === req.get("type");
    };

    const activeRequest = getActiveExpectedRequest(
      panelData,
      serviceRequestsTypes,
      requestType,
    );
    const activeRequestInfo = activeRequest
      ? activeRequest.merge(
          Map({
            type: requestType,
            icon: dynamicRequestType?.get("icon"),
            state: "pending_translation",
          }),
        )
      : null;

    return (
      <ContentWrapper>
        <Spinner isActive={isLoading} />
        <Heading $isBordered>
          <PanelBackButton onClick={handleClose} />
          <Title>{customTitle}</Title>
        </Heading>
        {isLoading || (
          <Content>
            <Form onSubmit={handleSubmitButtonClick}>
              {hasRequest && activeRequestInfo ? (
                <>
                  <RequestInfo request={activeRequestInfo} />
                  {activeRequestInfo.get("user_comment") && (
                    <UserComment>
                      <b>{t("common.comment")}: </b>
                      {activeRequestInfo.get("user_comment")}
                    </UserComment>
                  )}
                </>
              ) : (
                <div>
                  {showParagraph && <Paragraph>{paragraphText}</Paragraph>}
                  {showMessageIconBlock && (
                    <MessageWrapper>
                      {dynamicRequestType?.get("icon") && (
                        <RasterIcon icon={dynamicRequestType.get("icon")} />
                      )}
                      <Message>{t("serviceRequests.processTime")}</Message>
                    </MessageWrapper>
                  )}
                  {showComment && (
                    <Comment
                      name="comment"
                      value={values.comment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      tooltip={errors.comment}
                      invalid={!!errors.comment && !!touched.comment}
                      disabled={readOnly}
                    />
                  )}
                  {showPriceSection && (
                    <Price>
                      <PriceBlock
                        price={requestCost}
                        title={t("serviceRequests.requestCost")}
                      />
                    </Price>
                  )}
                  {isDynamicComplexRequest && dynamicRequestType && (
                    <>
                      {dynamicRequestType
                        .get("requestsList")
                        .map((req: any) => (
                          <GroupItem
                            key={req.get("type")}
                            $isHighlighted={true}
                          >
                            <GroupItemHeader>
                              <Checkbox
                                checked={getIsOptionSelected(req)}
                                onChange={() => handleGroupCheckBoxClick(req)}
                              />
                              {req.get("title")}
                            </GroupItemHeader>
                            <GroupItemCost $isHighlighted={true}>
                              {`${currencySymbol}${parseFloat(req.get("cost")).toFixed(2)}`}
                            </GroupItemCost>
                          </GroupItem>
                        ))}
                    </>
                  )}
                </div>
              )}
              {!readOnly && (
                <SubmitButton
                  onClick={handleSubmitButtonClick}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  color={hasRequest ? ButtonColor.Danger : ButtonColor.Primary}
                  variant={ButtonVariant.Filled}
                >
                  {submitTitle}
                </SubmitButton>
              )}
            </Form>
          </Content>
        )}
      </ContentWrapper>
    );
  },
);

export default withExpectedConnect(ExpectedServiceRequest);
